import type { LoadGrade } from "@/features/intake/api/types"

export interface RefractionInputs {
  moisture: number
  foreignMatter: number
  broken: number
  waste: number
  crabot: number
  mouldy: number
}

export interface ClassificationInputs {
  mouldy: number
  slaty: number
  germinated: number
  mites: number
  flatBeans: number
}

export type RefractionMetric =
  | "moisture"
  | "foreignMatter"
  | "broken"
  | "waste"
  | "crabot"
  | "mouldy"

// Three CCC grade drivers. `defectueuse` is a synthetic composite
// (germinated + mites + flatBeans) computed at evaluation time, not a
// raw lab field on the load.
export type ClassificationMetric = "mouldy" | "slaty" | "defectueuse"

export interface RefractionRefused {
  rejected: true
  reasonKey: RefractionMetric
  threshold: number
  value: number
}

// Refraction in v3 is expressed in absolute kg, proportional to the
// excess over each metric's clean ceiling, applied to the accepted
// weight. The `kg/MT` abstraction is gone.
export interface RefractionAccepted {
  rejected: false
  totalKg: number
  breakdown: Record<Exclude<RefractionMetric, "mouldy">, number>
}

export type RefractionResult = RefractionRefused | RefractionAccepted

// Refraction band ceilings (clean / reject). Mirrors rules.go.
export const REFRACTION_BANDS: Record<
  RefractionMetric,
  { clean: number; reject: number }
> = {
  moisture: { clean: 8, reject: 9 },
  foreignMatter: { clean: 1, reject: 1.5 },
  broken: { clean: 2, reject: 2.5 },
  waste: { clean: 1.5, reject: 2 },
  crabot: { clean: 3, reject: 3 },
  mouldy: { clean: 6, reject: 6 },
}

// Grade limits — three drivers only, each compared against g1/g2.
export const GRADE_LIMITS: Record<
  ClassificationMetric,
  { g1: number; g2: number }
> = {
  mouldy: { g1: 3, g2: 4 },
  slaty: { g1: 3, g2: 8 },
  defectueuse: { g1: 3, g2: 6 },
}

// Rounded to two decimals so the displayed per-metric kg sums match
// the persisted TotalKg byte-for-byte (mirrors backend rules.go).
function proportionalKg(
  value: number,
  cleanCeiling: number,
  acceptedKg: number,
): number {
  const excess = value - cleanCeiling
  if (excess <= 0) return 0
  return Math.round(((excess * acceptedKg) / 100) * 100) / 100
}

export function computeRefractionKg(
  values: RefractionInputs,
  acceptedKg: number,
): RefractionResult {
  const { moisture, foreignMatter, broken, waste, crabot, mouldy } = values

  if (moisture > 9) {
    return { rejected: true, reasonKey: "moisture", threshold: 9, value: moisture }
  }
  if (foreignMatter > 1.5) {
    return {
      rejected: true,
      reasonKey: "foreignMatter",
      threshold: 1.5,
      value: foreignMatter,
    }
  }
  if (broken > 2.5) {
    return { rejected: true, reasonKey: "broken", threshold: 2.5, value: broken }
  }
  if (waste > 2) {
    return { rejected: true, reasonKey: "waste", threshold: 2, value: waste }
  }
  if (crabot > 3) {
    return { rejected: true, reasonKey: "crabot", threshold: 3, value: crabot }
  }
  if (mouldy > 6) {
    return { rejected: true, reasonKey: "mouldy", threshold: 6, value: mouldy }
  }

  const breakdown: RefractionAccepted["breakdown"] = {
    moisture: proportionalKg(moisture, 8, acceptedKg),
    foreignMatter: proportionalKg(foreignMatter, 1, acceptedKg),
    broken: proportionalKg(broken, 2, acceptedKg),
    waste: proportionalKg(waste, 1.5, acceptedKg),
    crabot: 0,
  }
  const totalKg =
    Math.round(
      (breakdown.moisture +
        breakdown.foreignMatter +
        breakdown.broken +
        breakdown.waste +
        breakdown.crabot) *
        100,
    ) / 100

  return { rejected: false, totalKg, breakdown }
}

export function defectueuseSum(values: ClassificationInputs): number {
  return (
    (Number(values.germinated) || 0) +
    (Number(values.mites) || 0) +
    (Number(values.flatBeans) || 0)
  )
}

export interface GradeResult {
  grade: LoadGrade
  driver: { metric: ClassificationMetric; value: number; limit: number } | null
}

// classifyGradeWithDriver returns the grade plus the metric that drove
// the worst classification — useful for the UI ("Grade 2 — limited by
// défectueuse 4% > 3%").
export function classifyGradeWithDriver(
  values: ClassificationInputs,
): GradeResult {
  const checks: Array<{ metric: ClassificationMetric; value: number }> = [
    { metric: "mouldy", value: Number(values.mouldy) || 0 },
    { metric: "slaty", value: Number(values.slaty) || 0 },
    { metric: "defectueuse", value: defectueuseSum(values) },
  ]

  let worst: "g1" | "g2" = "g1"
  let driver: GradeResult["driver"] = null

  for (const { metric, value } of checks) {
    const lim = GRADE_LIMITS[metric]
    if (value > lim.g2) {
      return {
        grade: "SOUS_GRADE",
        driver: { metric, value, limit: lim.g2 },
      }
    }
    if (value > lim.g1 && worst === "g1") {
      worst = "g2"
      driver = { metric, value, limit: lim.g1 }
    }
  }
  return {
    grade: worst === "g1" ? "GRADE_1" : "GRADE_2",
    driver,
  }
}

export function classifyGrade(values: ClassificationInputs): LoadGrade {
  return classifyGradeWithDriver(values).grade
}
